import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, FileText, Printer } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ExportButton } from "../components/shared/ExportButton";

export default function MachineReferencePage({ onLogout, userRole, isFeatureEnabled, machineCategories = [] }) {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortField, setSortField] = useState("machine_number");
  const [sortDir, setSortDir] = useState("asc");
  const [printing, setPrinting] = useState(false);
  const navigate = useNavigate();

  const fetchMachines = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/machines?limit=1000`, { headers: getAuthHeaders() });
      setMachines(response.data);
    } catch {
      toast.error("Failed to load machine index");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMachines(); }, [fetchMachines]);

  // Build a color map from categories
  const catColorMap = {};
  machineCategories.forEach((c) => { catColorMap[c.name] = { color: c.color, bg: c.bg_color }; });

  const filtered = machines.filter((m) => {
    if (categoryFilter !== "all" && m.category !== categoryFilter) return false;
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      m.machine_number?.toLowerCase().includes(s) ||
      m.serial_number?.toLowerCase().includes(s) ||
      m.name?.toLowerCase().includes(s) ||
      m.description?.toLowerCase().includes(s) ||
      m.type?.toLowerCase().includes(s)
    );
  });

  const sorted = [...filtered].sort((a, b) => {
    const av = (a[sortField] || "").toLowerCase();
    const bv = (b[sortField] || "").toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };

  // Count by category
  const catCounts = {};
  machineCategories.forEach((c) => { catCounts[c.name] = machines.filter((m) => m.category === c.name).length; });
  const uncategorizedCount = machines.filter((m) => !m.category).length;

  const handlePrintAllQRCodes = async () => {
    if (machines.length === 0) { toast.error("No machines to print"); return; }
    setPrinting(true);
    try {
      const response = await axios.get(`${API}/machines/qrcodes/bulk`, { headers: getAuthHeaders() });
      const qrCodes = response.data.qr_codes;
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`<html><head><title>Asset Tags - Bulk Print</title><style>@page{size:letter;margin:.5in}body{font-family:Arial,sans-serif;margin:0;padding:0}.asset-tags{display:grid;grid-template-columns:repeat(2,1fr);gap:20px;padding:20px}.asset-tag{border:2px solid #000;padding:15px;text-align:center;background:#fff;page-break-inside:avoid}.machine-number{font-size:18px;font-weight:bold;margin-bottom:8px;font-family:monospace}.machine-name{font-size:12px;margin-bottom:8px}.identifiers{font-size:9px;margin-bottom:12px;font-family:monospace;color:#666}.location{font-size:9px;margin-bottom:12px;color:#666}img{width:150px;height:150px}@media print{.asset-tag{break-inside:avoid}}</style></head><body><div class="asset-tags">${qrCodes.map((qr) => `<div class="asset-tag"><div class="machine-number">${qr.machine_number}</div><div class="machine-name">${qr.name}</div><div class="identifiers">${qr.serial_number ? `SN: ${qr.serial_number}<br>` : ""}${qr.asset_number ? `Asset: ${qr.asset_number}<br>` : ""}</div>${qr.location ? `<div class="location">${qr.location}</div>` : ""}<img src="${qr.qr_code}" alt="QR Code" /></div>`).join("")}</div><script>window.onload=function(){setTimeout(function(){window.print()},500);window.onafterprint=function(){window.close()}};</script></body></html>`);
      printWindow.document.close();
      toast.success(`Generated ${qrCodes.length} QR codes for printing`);
    } catch { toast.error("Failed to generate QR codes"); }
    finally { setPrinting(false); }
  };

  const SortHeader = ({ field, label }) => (
    <th
      className="px-4 py-3 text-left text-xs font-bold text-[#0F172A] tracking-wider uppercase cursor-pointer hover:bg-[#F1F5F9] select-none"
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        {sortField === field && <span className="text-[#2563EB]">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );

  const getCatStyle = (catName) => {
    if (catColorMap[catName]) return { backgroundColor: catColorMap[catName].bg, color: catColorMap[catName].color };
    return { backgroundColor: "#F1F5F9", color: "#64748B" };
  };

  const MachineRow = ({ machine }) => (
    <tr className="hover:bg-[#FAFAFA] transition-colors duration-150" data-testid={`index-row-${machine.id}`}>
      <td className="px-4 py-3">
        <span className="inline-block px-2 py-0.5 text-xs font-bold tracking-wider" style={getCatStyle(machine.category)}>
          {machine.category || "\u2014"}
        </span>
      </td>
      <td className="px-4 py-3 text-sm font-mono font-bold text-[#2563EB]">{machine.machine_number}</td>
      <td className="px-4 py-3 text-sm text-[#0F172A]">{machine.name}</td>
      <td className="px-4 py-3 text-sm text-[#0F172A]">{machine.type}</td>
      <td className="px-4 py-3 text-sm text-[#64748B]">{machine.description || "\u2014"}</td>
      <td className="px-4 py-3 text-sm font-mono text-[#0F172A]">{machine.serial_number || <span className="text-[#CBD5E1]">{"\u2014"}</span>}</td>
      <td className="px-4 py-3 text-sm font-mono text-[#0F172A]">{machine.asset_number || <span className="text-[#CBD5E1]">{"\u2014"}</span>}</td>
      <td className="px-4 py-3 text-sm text-[#64748B]">{machine.location || "\u2014"}</td>
    </tr>
  );

  if (loading) {
    return (
      <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
        <div className="flex items-center justify-center h-64"><div className="text-[#64748B] font-mono">Loading machine index...</div></div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="machine-index-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-4xl text-[#0F172A] mb-2" data-testid="index-title">Machine Index</h1>
            <p className="text-[#64748B] text-sm">Category correlation with model, description, and serial number</p>
          </div>
          <div className="flex gap-2">
            <ExportButton endpoint="/export/machines" filename="machine_index_export.csv" />
            <Button onClick={handlePrintAllQRCodes} disabled={printing || machines.length === 0} className="bg-[#2563EB] hover:bg-[#0F172A] text-white rounded-none" data-testid="print-all-qr-button">
              <Printer size={16} className="mr-2" />{printing ? "Generating..." : "Print QR Codes"}
            </Button>
          </div>
        </div>

        {/* Summary cards — dynamic per category */}
        <div className={`grid grid-cols-1 gap-4`} style={{ gridTemplateColumns: `repeat(${machineCategories.length + 1}, minmax(0, 1fr))` }}>
          {machineCategories.map((cat) => (
            <div key={cat.name} className="bg-white border border-[#E2E8F0] p-4" data-testid={`count-card-${cat.name.toLowerCase()}`}>
              <div className="text-xs text-[#64748B] uppercase tracking-wider font-bold">{cat.label}</div>
              <div className="text-3xl font-mono font-black mt-1" style={{ color: cat.color }}>{catCounts[cat.name] || 0}</div>
            </div>
          ))}
          <div className="bg-white border border-[#E2E8F0] p-4" data-testid="total-count-card">
            <div className="text-xs text-[#64748B] uppercase tracking-wider font-bold">Total Machines</div>
            <div className="text-3xl font-mono font-black text-[#0F172A] mt-1">{machines.length}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white border border-[#E2E8F0] p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <label className="text-[#0F172A] font-medium text-sm mb-2 block">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#64748B]" size={18} />
                <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Machine #, serial, name, description..." className="pl-10 rounded-none border-[#E2E8F0] font-mono" data-testid="index-search-input" />
              </div>
            </div>
            <div>
              <label className="text-[#0F172A] font-medium text-sm mb-2 block">Category</label>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="h-10 px-3 rounded-none border border-[#E2E8F0] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]" data-testid="index-category-filter">
                <option value="all">All Categories</option>
                {machineCategories.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
              </select>
            </div>
            {machineCategories.map((cat) => (
              <Button key={cat.name} onClick={() => navigate(`/machines/${cat.name.toLowerCase()}`)} variant="outline" className="rounded-none" style={{ borderColor: cat.bg_color, color: cat.color }} data-testid={`goto-${cat.name.toLowerCase()}-button`}>
                {cat.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E2E8F0]">
          {sorted.length === 0 ? (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto text-[#64748B] mb-4" />
              <p className="text-[#64748B]">{searchTerm || categoryFilter !== "all" ? "No machines match your filters" : "No machines in database"}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="machine-index-table">
                <thead className="bg-[#FAFAFA] border-b-2 border-[#0F172A]">
                  <tr>
                    <SortHeader field="category" label="Cat." />
                    <SortHeader field="machine_number" label="Machine #" />
                    <SortHeader field="name" label="Model / Name" />
                    <SortHeader field="type" label="Type" />
                    <SortHeader field="description" label="Description" />
                    <SortHeader field="serial_number" label="Serial #" />
                    <th className="px-4 py-3 text-left text-xs font-bold text-[#0F172A] tracking-wider uppercase">Asset #</th>
                    <SortHeader field="location" label="Location" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  {sorted.map((m) => <MachineRow key={m.id} machine={m} />)}
                </tbody>
              </table>
            </div>
          )}
          <div className="px-4 py-3 bg-[#FAFAFA] border-t border-[#E2E8F0] text-xs text-[#64748B]">
            Showing {sorted.length} of {machines.length} machines
            {machineCategories.map((cat) => {
              const count = catCounts[cat.name] || 0;
              return count > 0 ? <span key={cat.name} className="ml-3 font-medium" style={{ color: cat.color }}>{count} {cat.name}</span> : null;
            })}
            {uncategorizedCount > 0 && <span className="ml-3 text-[#94A3B8]">{uncategorizedCount} uncategorized</span>}
          </div>
        </div>
      </div>
    </Layout>
  );
}
